import { configureStore } from '@reduxjs/toolkit'
import { persistStore, persistReducer } from 'redux-persist'

import { reducer } from '@vrw/data'
import { RedDataAction } from '@vrw/data/src/types'
import { LOGOUT_SUCCESS } from '@vrw/data/src/redux/auth/types'
import { RootState } from './types'
import { ENV } from '../config'

export interface Storage {
  getItem(key: string, ...args: Array<any>): any
  setItem(key: string, value: any, ...args: Array<any>): any
  removeItem(key: string, ...args: Array<any>): any
  keys?: Array<string>
  getAllKeys(cb?: any): any
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noopStorage = {
  getItem: () => {
    console.warn('Unable to get item: No storage method available')
  },
  setItem: () => {
    console.warn('Unable to set item: No storage method available')
  },
  removeItem: () => {
    console.warn('Unable to remove item: No storage method available')
  },
  keys: [],
  getAllKeys: () => {
    console.warn('Unable to get all keys: No storage method available')
  },
}

function hasStorage(storageType: string) {
  if (typeof self !== 'object' || !(storageType in self)) {
    return false
  }

  try {
    const storage = (self as unknown as { [key: string]: Storage })[storageType] as unknown as Storage
    const testKey = `redux-persist ${storageType} test`
    storage.setItem(testKey, 'test')
    storage.getItem(testKey)
    storage.removeItem(testKey)
  } catch (e) {
    console.warn({ hasStorage_Failed: e })
    if (process.env.NODE_ENV !== 'production') console.warn(`redux-persist ${storageType} test failed, persistence will be disabled.`)
    return false
  }
  return true
}

const getStorage = (type: string): Storage => {
  const storageType = `${type}Storage`
  if (hasStorage(storageType)) return (self as unknown as { [key: string]: Storage })[storageType]
  else {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`redux-persist failed to create sync storage. falling back to noop storage.`)
    }
    return noopStorage
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createWebStorage = (type: string): any => {
  const storage = getStorage(type)
  return {
    getItem: (key: string): Promise<string> => {
      return Promise.resolve(storage.getItem(key))
    },
    setItem: (key: string, item: string): Promise<void> => {
      return Promise.resolve(storage.setItem(key, item))
    },
    removeItem: (key: string): Promise<void> => {
      return Promise.resolve(storage.removeItem(key))
    },
  }
}

const storage = createWebStorage('local')

const persistConfig = {
  key: 'root',
  storage,
  blacklist: [
    'intro',
    'categories',
    'categoriesEarn',
    'categoriesPublic',
    'categoriesEarnPublic',
    'rewardsEarn',
    'rewardsPublic',
    'rewardsEarnPublic',
    'discovery',
    'discoveryEarn',
    'discoveryPublic',
    'discoveryEarnPublic',
    'pages',
    'promoCodes',
  ],
}

const rootReducer = (state: RootState | undefined, action: RedDataAction) => {
  let rootState = state
  if (action.type === LOGOUT_SUCCESS) {
    storage.removeItem('persist:root')
    rootState = undefined
  }
  return reducer(rootState, action)
}

const createStore = () => {
  // remove isRTKFullyMigrated expression and allow immutableCheck & serializableCheck once fully migrated!
  const isRTKFullyMigrated = false
  const persistedReducer: any = persistReducer<RootState>(persistConfig, rootReducer as any)
  const store = configureStore({
    reducer: persistedReducer,
    devTools: ENV === 'localhost' || ENV === 'dev' || ENV === 'staging',
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        immutableCheck: isRTKFullyMigrated,
        serializableCheck: isRTKFullyMigrated,
      }),
  })
  const persistor = persistStore(store)
  return { store, persistor }
}

const { store, persistor } = createStore()

export { store, persistor }
